import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Typography
} from '@vivaldis/antd-ui'
import { Divider } from '../../../../../../../Divider'
import { ImageUpload } from '../../../../../../components/ImageUpload'
import { Employee } from '../../../../../../types/Employee'
import { useForm } from '../../../../provider/FormProvider'
import { CountryType } from '../../../../../../types/CountryType'

interface Props {
  countries: CountryType[]
  employee: Employee
}

export const ResidencePermit = ({ countries, employee }: Props) => {
  const [t] = useTranslation('web_ui')

  const { values, setFieldError, setFieldTouched, setFieldValue } = useForm()

  // we show residence permit when user selected non-EEA country
  const isEeaMember = useMemo(() => {
    if (!values.nationalityCountryId) {
      return true
    }

    return !!countries.find(
      country => country.id === values.nationalityCountryId
    )?.eeaMember
  }, [countries, values.nationalityCountryId])

  if (!values.nationalityCountryId) {
    return null
  }

  if (isEeaMember) {
    return null
  }

  return (
    <>
      <Divider sectionSeparator />
      <Typography.Title level={5} marginBottom={16}>
        {t(
          'edit_employee_personal_information_form.steps.edit_manually.residence_permit_section_header'
        )}
      </Typography.Title>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="residencePermitFrontPictureFile"
            required
            label={t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_front_picture.label'
            )}
          >
            <ImageUpload
              defaultSrc={employee.workProfile?.residencePermitFrontPicture}
              fieldName={'residencePermitFrontPictureFile'}
              value={values.residencePermitFrontPictureFile}
              setFieldError={setFieldError}
              setFieldTouched={setFieldTouched}
              setFieldValue={setFieldValue}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="residencePermitBackPictureFile"
            required
            label={t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_back_picture.label'
            )}
          >
            <ImageUpload
              defaultSrc={employee.workProfile?.residencePermitBackPicture}
              fieldName={'residencePermitBackPictureFile'}
              value={values.residencePermitBackPictureFile}
              setFieldError={setFieldError}
              setFieldTouched={setFieldTouched}
              setFieldValue={setFieldValue}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="residencePermitCode"
            required
            label={t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_code.label'
            )}
          >
            <Input
              name="residencePermitCode"
              placeholder={t('form.required')}
              autoComplete="off"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="residencePermitExpiresOn"
            required
            label={t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_expires_on.label'
            )}
          >
            <DatePicker
              name="residencePermitExpiresOn"
              format="dd-MM-yyyy"
              placeholder={t('form.required')}
              style={{ width: '100%' }}
              autoComplete="off"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}
